import { useCallback, useState } from 'react';
import {
  CombinatorRendererProps,
  ControlElement,
  createCombinatorRenderInfos,
  createDefaultValue,
  isAnyOfControl,
  JsonSchema,
  Paths,
  rankWith,
} from '@jsonforms/core';
import { JsonFormsDispatch, withJsonFormsAnyOfProps } from '@jsonforms/react';
import {
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';

/**
 * Renders anyOf with a guard: if schema.anyOf is missing or not a non-empty
 * array, returns null to avoid createCombinatorRenderInfos(undefined) and the
 * resulting "Cannot read properties of undefined (reading 'map')" in the
 * default MaterialAnyOfRenderer. When valid, renders the same tab UI as
 * MaterialAnyOfRenderer.
 *
 * Selected tab is driven by the actual form data (indexOfFittingSchema) so
 * that when we switch tabs we set a fresh default and the UI only shows the
 * new tab after the data has updated—ensuring fields from the previous tab
 * are never shown.
 */
function SafeAnyOfGuardComponent({
  handleChange,
  schema,
  rootSchema,
  indexOfFittingSchema,
  visible,
  path,
  renderers,
  cells,
  uischema,
  uischemas,
  id: _id,
  data,
}: CombinatorRendererProps) {
  const theme = useTheme();
  // Fallback when data doesn't match any branch (e.g. empty or invalid)
  const [fallbackIndex, setFallbackIndex] = useState(0);
  // Drive selected tab from data so we never show a tab whose data doesn't match
  const selectedAnyOf =
    indexOfFittingSchema !== undefined ? indexOfFittingSchema : fallbackIndex;

  const anyOfArray = (schema as JsonSchema).anyOf;
  if (!Array.isArray(anyOfArray) || anyOfArray.length === 0) {
    return null;
  }

  const anyOf = 'anyOf';
  const anyOfRenderInfos = createCombinatorRenderInfos(
    anyOfArray,
    rootSchema,
    anyOf,
    uischema,
    path,
    uischemas
  );

  const controlElement = uischema as ControlElement;
  const rawLabel = controlElement.label;
  const controlLabel =
    typeof rawLabel === 'string'
      ? rawLabel
      : typeof rawLabel === 'object' && rawLabel !== null && 'text' in rawLabel
        ? (rawLabel as { text?: string }).text
        : '';

  const handleTabChange = useCallback(
    (_event: unknown, newIndex: number) => {
      const target = anyOfRenderInfos[newIndex];
      if (!target) return;
      const newValue = createDefaultValue(target.schema, rootSchema);
      // JSON Forms reducer no-ops when path is undefined/null; derive from control scope if needed
      const controlEl = uischema as ControlElement;
      const effectivePath =
        path !== undefined && path !== null && path !== ''
          ? path
          : controlEl?.scope
            ? Paths.fromScoped(controlEl)
            : '';
      setFallbackIndex(newIndex);
      if (effectivePath !== '') {
        handleChange(effectivePath, newValue);
      }
    },
    [anyOfRenderInfos, handleChange, path, rootSchema, data, uischema]
  );

  if (!visible) return null;

  return (
    <>
      {controlLabel ? (
        <Typography component="span" variant="subtitle2" sx={{ display: 'block', mb: 0.5 }}>
          {controlLabel}
        </Typography>
      ) : null}
      <Tabs
        value={Math.min(selectedAnyOf, anyOfRenderInfos.length - 1)}
        onChange={handleTabChange}
        textColor="secondary"
        indicatorColor="secondary"
        sx={{
          mt: 1,
          mb: 3,
          '& .MuiTabs-indicator': {
            backgroundColor: theme.palette.secondary.main,
          },
        }}
      >
        {anyOfRenderInfos.map((info, idx) => {
          const tabLabel = /^anyOf-\d+$/.test(info.label) ? `Option ${idx + 1}` : info.label;
          return (
            <Tab
              key={info.label}
              label={tabLabel}
              sx={{
                textTransform: 'none',
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: theme.palette.secondary.main,
                  fontWeight: 'bold',
                },
              }}
            />
          );
        })}
      </Tabs>
      {anyOfRenderInfos.map(
        (info, idx) =>
          selectedAnyOf === idx && (
            <JsonFormsDispatch
              key={`${path}-${idx}-${info.label}`}
              schema={info.schema}
              uischema={info.uischema}
              path={path}
              renderers={renderers}
              cells={cells}
            />
          )
      )}
    </>
  );
}

/** Rank 5 so we take over all anyOf; we no-op or render safely. */
export const safeAnyOfGuardTester = rankWith(5, isAnyOfControl);

export default withJsonFormsAnyOfProps(SafeAnyOfGuardComponent);
